import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, InputNumber, Message, Panel, useToaster } from "rsuite";
import Header from "../../../layouts/Header";
import PageBase from "../../../components/PageBase";
import { createOrder } from "../../../service/OrderService";
import { authUserData } from "../../../service";
import type { Order } from "../../../my-type";
import { OrderStatus, OrderType, SourceChannel } from "../../../my-enum";

const BlindCreate: React.FC = () => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const authUser = authUserData();

  const [rideFee, setRideFee] = useState<number | null>(null);
  const [carLicenseNo, setCarLicenseNo] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const header = useMemo(() => (
    <Header
      HeaderTitle="快速建立付款訂單"
      isShowRefresh={false}
      OnReturn={() => navigate("/rider")}
      OnRefresh={() => { }}
    />
  ), [navigate]);

  const onCreateOrder = async () => {
    if (!authUser) {
      toaster.push(
        <Message showIcon type="error" closable>
          找不到使用者資訊，請重新登入
        </Message>,
        { placement: "topCenter" }
      );
      return;
    }

    const amount = Number(rideFee);
    if (!Number.isFinite(amount) || amount <= 0) {
      toaster.push(
        <Message showIcon type="error" closable>
          請輸入有效金額
        </Message>,
        { placement: "topCenter" }
      );
      return;
    }

    const trimmedCarLicenseNo = carLicenseNo.trim();
    if (!trimmedCarLicenseNo) {
      toaster.push(
        <Message showIcon type="error" closable>
          請輸入司機車牌號碼
        </Message>,
        { placement: "topCenter" }
      );
      return;
    }

    const order: Order = {
      id: 0,
      orderID: "x",
      userID: authUser.userId,
      userUID: authUser.id,
      orderType: OrderType.接送,
      address: "現場叫車",
      destinationAddress: null,
      terminal: "",
      rideTime: new Date(),
      estArriveTime: new Date(),
      flightNo: "",
      flightTime: null,
      rideTimeValid: true,
      vehicleID: "",
      luggages: "",
      luggageNote: trimmedCarLicenseNo,
      passengerName: authUser.displayName || authUser.userId,
      passengerPhone: "",
      passengerID: "",
      passengerAdult: 1,
      passengerBaby: 0,
      driverUID: 0,
      driver: null,
      rideFee: Math.round(amount),
      status: OrderStatus.派車中,
      createdAt: new Date(),
      completedAt: null,
      teamCode: authUser.team.code,
      signatureFileName: null,
      rentalStartTime: null,
      rentalEndTime: null,
      paymentPaid: false,
      email: authUser.email || "",
      isAdminOrder: false,
      fromUserUID: null,
      driverCommission: null,
      viaPoints: null,
      specialRequests: null,
      meetAndGreetDetails: null,
      supplierId: 1,
      sourceChannel: SourceChannel.手動,
      externalBookingNo: null,
      chatApp: null,
      chatId: null,
      assignedCarModel: null,
      isBlindOrder: true
    };

    setSubmitting(true);
    const created = await createOrder(order);
    setSubmitting(false);

    if (!created?.orderID) {
      toaster.push(
        <Message showIcon type="error" closable>
          建立訂單失敗，請稍後再試
        </Message>,
        { placement: "topCenter" }
      );
      return;
    }

    navigate(`/rider/order/${created.orderID}/payment/ecpay/credit-card`);
  };

  return (
    <PageBase
      header={header}
      content={(
        <Panel bordered style={{ maxWidth: 520, margin: "24px auto" }}>
          <h5>快速付款</h5>
          <p style={{ color: "#666" }}>輸入本次車資後建立訂單，並直接前往 ECPay 付款。</p>
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: "left", marginBottom: 8 }}>車資金額（NTD）</div>
            <InputNumber
              min={1}
              step={10}
              value={rideFee}
              onChange={(value) => setRideFee(value === null ? null : Number(value))}
              style={{ width: "100%" }}
              placeholder="請輸入金額"
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: "left", marginBottom: 8 }}>司機車牌號碼</div>
            <Input
              value={carLicenseNo}
              onChange={setCarLicenseNo}
              placeholder="請輸入車牌號碼"
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <Button appearance="primary" block onClick={onCreateOrder} loading={submitting}>
              建立訂單並前往付款
            </Button>
          </div>
        </Panel>
      )}
      showSide
    />
  );
};

export default BlindCreate;
